<template>
  <div class="w-full flex justify-center pt-6 pb-24 lg:px-6 bg-slate-200">
    <div
      class="w-full flex flex-col bg-pure-white border border-slate-400 rounded-3xl"
    >
      <div
        class="pt-10 pb-4 lg:px-10 px-4 flex items-center justify-between border-b border-slate-400"
      >
        <div class="flex items-center gap-2">
          <BlTextTitle4 text="Files" />
          <BlBubbleCount :count="totalClientDocuments || 0" />
        </div>
        <input
          type="file"
          name="upload_client_document"
          id="upload_client_document"
          hidden
          multiple
          @change="onButtonUpload"
        />
        <BlInputButton
          icon="plus"
          label="Upload files"
          @clicked="openUploadDoc"
        />
      </div>
      <div
        class="lg:px-10 px-4 lg:pt-6 pt-4 pb-10 flex flex-col lg:gap-6 gap-4"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <BlInputSearch
              class="w-80"
              :input="searchInput"
              @changed="onInputSearch"
            />
            <div class="w-40">
              <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
            </div>
            <BlSort
              class="w-60"
              v-model="sortInput"
              :options="sortOptions"
              :defaultValue="defaultSort"
              dropDirection="left"
              @change="onChangeSort($event)"
            />
          </div>
          <div
            class="py-1 px-1 flex bg-slate-400"
            :style="{ borderRadius: '8px', height: '2.375rem' }"
          >
            <div
              v-for="opt in modeOptions"
              :key="opt.value"
              class="h-full w-30 flex items-center justify-center gap-1 flex-shrink-0 rounded cursor-pointer transition-all duration-300"
              :class="mode === opt.value ? 'bg-pure-white' : 'bg-slate-400'"
              @click="mode = opt.value"
            >
              <BlIcon :icon="opt.icon" color="slate-6" class="w-4 h-4" />
              <BlTextSubtitle3 :text="opt.label" />
            </div>
          </div>
        </div>

        <div v-if="mode === 'thumbnail'">
          <div v-if="fetchLoading" class="w-full h-120 grid place-items-center">
            <div class="blum-loader"></div>
          </div>
          <div
            v-else-if="!clientDocuments.length"
            class="w-full h-120 text-center flex flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-slate-500"
            @dragenter.prevent="dropActive = true"
            @dragover.prevent="dropActive = true"
            @dragleave.prevent="dropActive = false"
            @drop.prevent="onDropUpload"
            :class="{ 'bg-slate-200': dropActive }"
          >
            <div
              class="h-10 w-10 flex items-center justify-center flex-shrink-0 bg-light-purple-100 rounded-full"
            >
              <BlIcon
                icon="download"
                class="h-6 w-6"
                :style="{ rotate: '180deg' }"
              />
            </div>
            <div class="flex items-center gap-1">
              <BlTextBody1 text="Drag and drop your files here or" />
              <div @click="openUploadDoc">
                <BlTextBody1
                  text="click to upload."
                  class="font-bold underline text-light-purple cursor-pointer"
                />
              </div>
            </div>
            <div>
              <BlTextBody1 text=" Each file must not exceed 25 MB." />
            </div>
          </div>
          <div
            v-else
            class="relative min-h-120 grid grid-cols-4 gap-4"
            @dragenter.prevent="dropActive = true"
            @dragover.prevent="dropActive = true"
          >
            <div
              class="absolute top-0 left-0 z-10 h-full w-full min-h-120 text-center flex-col items-center justify-center gap-2 bg-slate-200 rounded border-2 border-dashed border-slate-500"
              @dragenter.prevent="dropActive = true"
              @dragover.prevent="dropActive = true"
              @dragleave.prevent="dropActive = false"
              @drop.prevent="onDropUpload"
              :class="{ hidden: !dropActive, flex: dropActive }"
            >
              <div
                class="h-10 w-10 flex items-center justify-center flex-shrink-0 bg-light-purple-100 rounded-full"
              >
                <BlIcon
                  icon="download"
                  class="h-6 w-6"
                  :style="{ rotate: '180deg' }"
                />
              </div>
              <div>
                <BlTextBody1 text="Drag and drop your files here" />
              </div>
              <div>
                <BlTextBody1 text=" Each file must not exceed 25 MB." />
              </div>
            </div>
            <ItemGrid
              v-for="(document, idx) in clientDocuments"
              :key="document.id"
              :document="document"
              :checked="!!selectedDocument.find(i => i.id === document.id)"
              :isLast="idx + 1 === clientDocuments.length"
              @change-selected="onChangeSelected(document.id)"
              @after-commit="fetchDocuments(false)"
            />
          </div>
        </div>

        <div
          v-if="mode === 'table'"
          class="overflow-x-auto scrollbar-lg border border-slate-400 rounded"
        >
          <div id="files-header" class="flex">
            <div class="flex border-slate-400 rounded-tl">
              <div class="w-14 px-4 py-4 flex-shrink-0 bg-slate-200 rounded-tl">
                <BlInputCheck
                  :checked="checked"
                  :indeterminate="indeterminate"
                  @changed="onChangeSelectedAll"
                />
              </div>
            </div>
            <div class="w-112 flex-grow px-4 py-3 flex-shrink-0 bg-slate-200">
              <BlTextSubtitle2 text="File name" class="text-slate-700" />
            </div>
            <div class="w-50 px-4 py-3 flex-shrink-0 bg-slate-200">
              <BlTextSubtitle2 text="Type" class="text-slate-700" />
            </div>
            <div class="w-50 px-4 py-3 flex-shrink-0 bg-slate-200">
              <BlTextSubtitle2 text="Uploaded on" class="text-slate-700" />
            </div>
            <div class="w-50 px-4 py-3 flex-shrink-0 bg-slate-200">
              <BlTextSubtitle2 text="Uploaded by" class="text-slate-700" />
            </div>
            <div class="flex sticky right-0 z-10 bg-pure-white">
              <div
                class="w-60 px-4 py-3 rounded-tr flex-shrink-0 bg-slate-200"
              ></div>
            </div>
          </div>
          <div v-if="fetchLoading" class="w-full h-120 grid place-items-center">
            <div class="blum-loader"></div>
          </div>
          <div v-else-if="!clientDocuments.length" class="h-120 py-4 px-4">
            <div
              class="h-full w-full text-center flex flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-slate-500"
              @dragenter.prevent="dropActive = true"
              @dragover.prevent="dropActive = true"
              @dragleave.prevent="dropActive = false"
              @drop.prevent="onDropUpload"
              :class="{ 'bg-slate-200': dropActive }"
            >
              <div
                class="h-10 w-10 flex items-center justify-center flex-shrink-0 bg-light-purple-100 rounded-full"
              >
                <BlIcon
                  icon="download"
                  class="h-6 w-6"
                  :style="{ rotate: '180deg' }"
                />
              </div>
              <div class="flex items-center gap-1">
                <BlTextBody1 text="Drag and drop your files here or" />
                <div @click="openUploadDoc">
                  <BlTextBody1
                    text="click to upload."
                    class="font-bold underline text-light-purple cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <BlTextBody1 text=" Each file must not exceed 25 MB." />
              </div>
            </div>
          </div>
          <div
            v-else
            class="relative min-h-120"
            @dragenter.prevent="dropActive = true"
            @dragover.prevent="dropActive = true"
          >
            <div
              class="absolute top-0 left-0 z-10 h-full w-full px-4 py-4 bg-pure-white"
              :class="{ hidden: !dropActive, flex: dropActive }"
            >
              <div
                class="h-full w-full text-center flex-col flex items-center justify-center gap-2 bg-slate-200 rounded border-2 border-dashed border-slate-500"
                @dragenter.prevent="dropActive = true"
                @dragover.prevent="dropActive = true"
                @dragleave.prevent="dropActive = false"
                @drop.prevent="onDropUpload"
              >
                <div
                  class="h-10 w-10 flex items-center justify-center flex-shrink-0 bg-light-purple-100 rounded-full"
                >
                  <BlIcon
                    icon="download"
                    class="h-6 w-6"
                    :style="{ rotate: '180deg' }"
                  />
                </div>
                <div>
                  <BlTextBody1 text="Drag and drop your files here" />
                </div>
                <div>
                  <BlTextBody1 text=" Each file must not exceed 25 MB." />
                </div>
              </div>
            </div>
            <ItemList
              v-for="(document, idx) in clientDocuments"
              :key="document.id"
              :document="document"
              :checked="!!selectedDocument.find(i => i.id === document.id)"
              :isLast="idx + 1 === clientDocuments.length"
              @change-selected="onChangeSelected(document.id)"
              @after-commit="fetchDocuments(false)"
            />
          </div>
        </div>

        <BlInputPagination
          v-if="totalClientDocuments > 25"
          :page="page"
          :perPage="perPage"
          :total="totalClientDocuments"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>

      <div
        class="fixed z-50 py-2 min-h-16 w-full max-w-200 px-6 flex items-center justify-between gap-2 bg-white rounded border border-slate-400 shadow-lg transition-all duration-500"
        :class="[selectedDocument.length ? 'bottom-16' : '-bottom-16']"
        :style="{
          left: '50%',
          transform: 'translateX(-50%)'
        }"
      >
        <div class="flex items-center gap-2 truncate">
          <BlInputButton
            type="plain"
            icon="close"
            @clicked="selectedDocument = []"
          />
          <div class="truncate">
            <BlTextSubtitle3
              :text="`${selectedDocument.length} file(s) selected`"
              class="text-slate-800"
            />
          </div>
        </div>
        <div v-if="selectedDocument.length" class="flex items-center gap-2">
          <BlInputButton
            :label="`Delete (${selectedDocument.length})`"
            type="outlined-error"
            dense
            :loading="deleteLoading"
            @clicked="deleteOpened = true"
          />
          <BlInputButton
            :label="`Download (${selectedDocument.length})`"
            dense
            :loading="downloadLoading"
            @clicked="onDownloadSelected"
          />
        </div>
      </div>
    </div>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete file"
      :label="`Delete ${selectedDocument.length} file(s)`"
      type="error"
      :submitLoading="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onDeleteSelected"
    >
      <div
        class="px-4 lg:px-8 overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 14rem)' }"
      >
        <div>
          <BlTextBody2
            text="You are about to delete the following file(s) from your client:"
          />
        </div>
        <div
          v-for="(document, idx) in selectedDocument"
          :key="idx"
          class="flex items-center gap-2 px-2"
          :title="document.filename"
        >
          <div class="rounded h-1 w-1 bg-slate-800 flex-shrink-0"></div>
          <BlTextBody2 :text="document.filename" class="truncate" />
        </div>
        <div>
          <BlTextBody2
            text="Are you sure you want to proceed? This action is irreversible. "
          />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import ItemGrid from "./file/ItemGrid.vue";
import ItemList from "./file/ItemList.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextBody3 from "../BlTextBody3.vue";
import BlIcon from "../BlIcon.vue";
import BlChip from "../BlChip.vue";
import BlSort from "../BlSort.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlInputCheck from "../BlInputCheck.vue";
import BlInputPagination from "../BlInputPagination.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "ClientFile",
  components: {
    ItemGrid,
    ItemList,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlTextBody3,
    BlIcon,
    BlChip,
    BlSort,
    BlBubbleCount,
    BlInputButton,
    BlInputSearch,
    BlInputFilter,
    BlInputCheck,
    BlInputPagination,
    BlBackdrop
  },
  props: {
    id: { tyep: String | Number },
    uploadProgress: { type: Array }
    // @set-upload-progress
  },
  data() {
    const defaultFilter = [[], { start_date: "", end_date: "" }, []];
    const defaultSort = { primary: "name", secondary: "asc" };
    return {
      page: 1,
      perPage: 25,
      fetchLoading: false,
      dropActive: false,
      mode: "thumbnail",
      modeOptions: [
        { value: "thumbnail", icon: "picture-double", label: "Thumbnail" },
        { value: "table", icon: "table", label: "Table" }
      ],
      //
      searchInput: { model: "", placeholder: "Search by name" },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          {
            name: "Type",
            kind: "multi",
            options: [
              { value: "audio", name: "Audio" },
              { value: "document", name: "Document" },
              { value: "image", name: "Image" },
              { value: "video", name: "Video" },
              { value: "other", name: "Other" }
            ]
          },
          { name: "Uploaded on", kind: "date-custom" },
          {
            name: "Uploaded by",
            kind: "multi",
            options: [],
            url: `/api/v1/users?sort=name_asc&status=active&query=`,
            fetchPage: 1,
            dataKey: "users",
            countKey: "total_users",
            optionKeys: { value: "id", label: "name" }
          }
        ]
      },
      defaultSort,
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "name", label: "File name" },
          { value: "date", label: "Uploaded on" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      },
      //
      selectedDocument: [],
      downloadLoading: false,
      deleteOpened: false,
      deleteLoading: false
    };
  },
  computed: {
    ...mapGetters(["clientDocuments", "totalClientDocuments", "client"]),
    checked() {
      if (!this.clientDocuments.length) return false;
      const ids = this.selectedDocument.map(i => i.id);
      return this.clientDocuments.every(a => ids.includes(a.id));
    },
    indeterminate() {
      if (!this.clientDocuments.length) return false;
      const ids = this.selectedDocument.map(i => i.id);
      return (
        this.clientDocuments.findIndex(a => ids.includes(a.id)) > -1 &&
        !this.checked
      );
    }
  },
  watch: {},
  mounted() {
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach(eventName => {
      document.body.addEventListener(eventName, this.preventDefaults);
    });

    const searchParams = this.$route.query;

    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }

    let filter = [[], { start_date: "", end_date: "" }, []];
    if (searchParams["type"]) {
      const arr = searchParams["type"].split(",");
      filter[0] = arr.map(i => ({ value: i }));
    }
    if (searchParams["start_date"]) {
      filter[1].start_date = searchParams["start_date"];
    }
    if (searchParams["end_date"]) {
      filter[1].end_date = searchParams["end_date"];
    }
    if (searchParams["user_ids"]) {
      const arr = searchParams["user_ids"].split(",");
      filter[2] = arr.map(i => ({ value: Number(i) }));
    }
    this.filterInput.model = filter;

    // prettier-ignore
    const sorts = {
      name_asc: { primary: "name", secondary: "asc" },
      name_desc: { primary: "name", secondary: "desc" },
      oldest: { primary: "date", secondary: "asc" },
      newest: { primary: "date", secondary: "desc" }
    };
    if (searchParams["sort"]) {
      const sort = searchParams["sort"] || "";
      if (sort && sorts[sort]) {
        this.sortInput = sorts[sort];
      }
    }

    this.fetchDocuments();
  },
  destroyed() {
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach(eventName => {
      document.body.removeEventListener(eventName, this.preventDefaults);
    });
  },
  methods: {
    ...mapActions([
      "getClientDocuments",
      "uploadClientDocument",
      "deleteClientDocument",
      "downloadClientDocument"
    ]),
    preventDefaults(e) {
      e.preventDefault();
    },
    fetchDocuments(loading = true) {
      if (loading) this.fetchLoading = true;

      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const types = this.filterInput.model.at(0) || [];
      const dates = this.filterInput.model.at(1) || {
        start_date: "",
        end_date: ""
      };
      const users = this.filterInput.model.at(2) || [];
      const sort = this.sortInput;

      if (query) params += `&query=${query}`;
      if (types.length > 0) {
        params += `&type=${types.map(a => a.value).join(",")}`;
      }
      if (dates.start_date) {
        params += `&start_date=${dates.start_date}`;
      }
      if (dates.end_date) {
        params += `&end_date=${dates.end_date}`;
      }
      if (users.length > 0) {
        params += `&user_ids=${users.map(a => a.value).join(",")}`;
      }

      // - sort mapping -
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // date, ascending -> oldest
      // date, descending -> newest
      // prettier-ignore
      const sorts = {
        name: { asc: "name_asc", desc: "name_desc" },
        date: { asc: "oldest", desc: "newest" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;

      this.getClientDocuments({ client_id: this.id, params }).then(
        ({ success }) => {
          this.fetchLoading = false;
          // if (!success) return;
        }
      );

      // sync url path for page
      if (!loading) return;
      const incomingPath = `/clients/${this.id}/file${params}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchDocuments();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchDocuments();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchDocuments();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchDocuments();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchDocuments();
    },
    //
    openUploadDoc() {
      const input = document.getElementById("upload_client_document");
      if (input) input.click();
    },
    onDropUpload(e) {
      this.dropActive = false;
      const files = e.dataTransfer.files;
      if (files && files.length) {
        this.onUploadFiles(files);
      }
    },
    onButtonUpload(e) {
      const files = e.target.files;
      if (files && files.length) {
        this.onUploadFiles(files);
      }
    },
    async onUploadFiles(files) {
      this.uploadDocumentLoading = true;
      const maxFileSize = 26214400;

      const regProgress = [];
      for (let idx = 0; idx < files.length; idx++) {
        const exceedLimit = files[idx].size > maxFileSize;
        regProgress.push({
          name: files[idx].name,
          size: files[idx].size,
          exceed_limit: exceedLimit,
          started: false,
          uploading: false,
          success: false
        });
      }
      const newRegProgress = [...this.uploadProgress, ...regProgress];
      this.$emit("set-upload-progress", newRegProgress);

      for (let idx = 0; idx < files.length; idx++) {
        const formData = new FormData();
        const filename = files[idx].name;
        const filesize = files[idx].size;
        formData.append(`documents[]`, files[idx]);
        const data = {
          client_id: this.client.id,
          documents: formData
        };
        const arr = [...newRegProgress];
        const progressIdx = arr.findIndex(
          i => i.name === filename && i.size === filesize
        );
        if (progressIdx > -1) {
          const progress = arr[progressIdx];
          if (!progress.exceed_limit && !progress.started) {
            arr[progressIdx].uploading = true;
            arr[progressIdx].started = true;

            const { success } = await this.uploadClientDocument(data);
            arr[progressIdx].uploading = false;
            if (!success) {
              arr[progressIdx].success = false;
            } else {
              arr[progressIdx].success = true;
              this.fetchDocuments(false);
            }
          } else {
            arr[progressIdx].uploading = false;
          }
        }
        this.$emit("set-upload-progress", [...arr]);
        // if (!success) return;
      }
    },
    //
    onChangeSelected(id) {
      const ids = this.selectedDocument.map(i => i.id);
      if (ids.includes(id)) {
        this.selectedDocument.splice(ids.indexOf(id), 1);
      } else {
        const doc = this.clientDocuments.find(i => i.id === id);
        if (doc) this.selectedDocument = [...this.selectedDocument, doc];
      }
    },
    onChangeSelectedAll() {
      if (this.selectedDocument.length >= this.clientDocuments.length) {
        this.selectedDocument = [];
      } else if (this.clientDocuments) {
        this.selectedDocument = [...this.clientDocuments];
      }
    },
    async onDeleteSelected() {
      this.deleteLoading = true;
      const data = {
        client_id: this.id,
        ids: this.selectedDocument.map(i => i.id)
      };
      const { success } = await this.deleteClientDocument(data);
      if (!success) {
        this.deleteLoading = false;
        return;
      }
      await this.fetchDocuments(false);
      this.deleteLoading = false;
      this.selectedDocument = [];
      this.deleteOpened = false;
    },
    async onDownloadSelected() {
      this.downloadLoading = true;
      const data = {
        client_id: this.id,
        ids: this.selectedDocument.map(i => i.id)
      };
      this.downloadClientDocument(data)
        .then(res => {
          this.downloadLoading = false;
          const href = URL.createObjectURL(res.data);

          // generate filename
          function getFormattedTime() {
            const today = new Date();
            const y = today.getFullYear();
            // JavaScript months are 0-based.
            const m = today.getMonth() + 1;
            const d = today.getDate();
            const h = today.getHours();
            const mi = today.getMinutes();
            const s = today.getSeconds();
            return `${y}${m}${d}T${h}${mi}${s}`;
          }

          let filename = this.selectedDocument[0]
            ? this.selectedDocument[0].filename
            : "document";
          if (this.selectedDocument.length > 1) {
            const clientName = this.client ? this.client.name : "client";
            const suffix = getFormattedTime();
            filename = `documents_${clientName}_${suffix}.zip`;
          }

          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          this.selectedDocument = [];
        })
        .catch(err => {
          console.error(err);
          this.downloadLoading = false;
          this.selectedDocument = [];
        });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/ClientFile.vue